import CircularLoader from 'components/CircularLoader.tsx';
import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import { useAdornmentGetters } from 'components/forms/inputs/rhf-inputs/useAdornmentGetters.ts';
import { usePaginatedList } from 'components/forms/inputs/rhf-inputs/usePaginatedList.ts';
import { SearchIcon } from 'components/icons';
import { Checkbox } from 'components/ui/checkbox';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll.ts';
import get from 'lodash/get';
import { ReactNode, useMemo, useState } from 'react';
import { foldForSearch } from 'utils/foldForSearch';
import { t } from 'utils/getTranslations.ts';
import { cn } from 'utils/cn';
import { optionDisplayLabel, useLastNameFirst } from 'utils/nameOrder';
import { useSameNameEmail } from 'utils/sameNameEmail';
import {
    DynamicMultiselectFilterForm,
    MainFilterInForm,
    MultiselectFilterForm,
    NULL_FILTER_SENTINEL,
    QuickFilterForm,
    SelectOption,
} from '../../ListTableTypes.tsx';
import { withContactAdornments } from './ContactDynamicMultiSelect.tsx';

type Props = {
    filter: MultiselectFilterForm | DynamicMultiselectFilterForm;
    /** The field's name, for the search box. */
    label: string;
    quickFilters: QuickFilterForm;
    mainFiltersInForm: MainFilterInForm[];
    onChange: (selected: SelectOption[]) => void;
};

/**
 * The new design's value list for a multi-value field in + Filter: a search field over an always-open list
 * of checkboxes, the chosen options first. A field of fixed options searches them here; an entity field
 * searches its own endpoint, a page at a time. The classic drawer keeps its multi-select.
 */
export default function FilterChecklist(props: Props) {
    return props.filter.type === 'dynamicmultiselect' ? (
        <DynamicChecklist {...props} filter={props.filter} />
    ) : (
        <StaticChecklist {...props} filter={props.filter} />
    );
}

const isNullOption = (option: SelectOption) => option.value === NULL_FILTER_SENTINEL;

/** The chosen options, then the others, in the order the list first showed them. */
const chosenFirst = (options: SelectOption[], selected: SelectOption[]) => {
    const isChosen = (option: SelectOption) => selected.some((chosen) => chosen.value === option.value);
    return [...options.filter(isChosen), ...options.filter((option) => !isChosen(option))];
};

function StaticChecklist({ filter, label, onChange }: Props & { filter: MultiselectFilterForm }) {
    const [query, setQuery] = useState('');
    // Ordered once: a box ticked while the list is open stays where it was clicked.
    const [ordered] = useState(() => chosenFirst(filter.options, filter.selected));
    const needle = foldForSearch(query);
    const options = needle ? ordered.filter((option) => foldForSearch(String(option.label)).includes(needle)) : ordered;

    return (
        <ChecklistView
            label={label}
            query={query}
            onQueryChange={setQuery}
            options={options}
            selected={filter.selected}
            onChange={onChange}
        />
    );
}

function DynamicChecklist({
    filter,
    label,
    quickFilters,
    mainFiltersInForm,
    onChange,
}: Props & { filter: DynamicMultiselectFilterForm }) {
    const { paginated } = filter;
    const lastNameFirst = useLastNameFirst();
    const [query, setQuery] = useState('');
    const { dataToDisplay, handleLoadMore, handleSearchChange, hasNextPage, isLoading, isError } = usePaginatedList({
        ...paginated,
        quickFilters,
        mainFilters: mainFiltersInForm,
    });
    // The options chosen when the list opened lead it until a search narrows it.
    const [chosenOnOpen] = useState(() => filter.selected.filter((option) => !isNullOption(option)));
    const fetched = useMemo<SelectOption[]>(
        () =>
            dataToDisplay.map((item) => ({
                ...item,
                value: get(item, paginated.getOptionIdentifier),
                label: optionDisplayLabel(item, lastNameFirst, get(item, paginated.getOptionLabel)),
            })),
        [dataToDisplay, paginated.getOptionIdentifier, paginated.getOptionLabel, lastNameFirst]
    );
    // Each keeps its place but takes its label as it reads now: a URL's or a view's ids may resolve after opening.
    const leading = chosenOnOpen.map(
        (chosen) =>
            filter.selected.find((option) => option.value === chosen.value && option.label) ??
            fetched.find((option) => option.value === chosen.value) ??
            chosen
    );
    const options = query
        ? fetched
        : [...leading, ...fetched.filter((option) => !chosenOnOpen.some((chosen) => chosen.value === option.value))];
    const getSameNameEmail = useSameNameEmail(options);
    // A contact field shows what the classic drawer's contact multi-select does.
    const adorned = filter.subType === 'contact' ? withContactAdornments(filter) : filter;
    const { getStartAdornment, getEndAdornment } = useAdornmentGetters(
        adorned.getOptionStartAdornment,
        adorned.getOptionEndAdornment
    );

    const [listEl, setListEl] = useState<HTMLDivElement | null>(null);
    const [loaderEl, setLoaderEl] = useState<HTMLDivElement | null>(null);
    useInfiniteScroll(
        loaderEl,
        { observer: { root: listEl, rootMargin: '75px', threshold: 0 }, disabled: isError, isLoading },
        handleLoadMore
    );

    return (
        <ChecklistView
            label={label}
            query={query}
            onQueryChange={(value) => {
                setQuery(value);
                handleSearchChange(value);
            }}
            options={options}
            selected={filter.selected}
            onChange={onChange}
            listRef={setListEl}
            isLoading={isLoading}
            renderLabel={(option) => {
                const start = getStartAdornment(option);
                const end = getEndAdornment(option);
                const sameNameEmail = getSameNameEmail(option);
                return (
                    <>
                        {start || start === 0 ? <span className="flex shrink-0 items-center">{start}</span> : null}
                        <span className={cn('min-w-0 truncate', sameNameEmail && 'flex-1')}>
                            <PersonOptionLabel
                                option={option}
                                label={String(option.label)}
                                sameNameEmail={sameNameEmail}
                            />
                        </span>
                        {end || end === 0 ? <span className="flex shrink-0 items-center">{end}</span> : null}
                    </>
                );
            }}
            after={
                hasNextPage ? (
                    <div ref={setLoaderEl} className="relative py-2">
                        <CircularLoader className="static [&>span]:size-6" />
                    </div>
                ) : null
            }
        />
    );
}

function ChecklistView({
    label,
    query,
    onQueryChange,
    options,
    selected,
    onChange,
    listRef,
    isLoading = false,
    renderLabel = (option) => <span className="min-w-0 truncate">{String(option.label)}</span>,
    after,
}: {
    label: string;
    query: string;
    onQueryChange: (query: string) => void;
    options: SelectOption[];
    selected: SelectOption[];
    onChange: (selected: SelectOption[]) => void;
    listRef?: React.Ref<HTMLDivElement>;
    isLoading?: boolean;
    renderLabel?: (option: SelectOption) => ReactNode;
    after?: ReactNode;
}) {
    const isChecked = (option: SelectOption) => selected.some((chosen) => chosen.value === option.value);
    const toggle = (option: SelectOption, checked: boolean) =>
        onChange(checked ? [...selected, option] : selected.filter((chosen) => chosen.value !== option.value));
    const searchLabel = t('search-records', { record: label.toLocaleLowerCase() });

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-1.5">
            <InputGroup className="bg-paper border-border-control h-[34px] shrink-0 shadow-none">
                <InputGroupAddon align="inline-start">
                    <SearchIcon className="size-3.5" />
                </InputGroupAddon>
                <InputGroupInput
                    placeholder={searchLabel}
                    aria-label={searchLabel}
                    value={query}
                    onChange={(event) => onQueryChange(event.target.value)}
                />
            </InputGroup>
            <div ref={listRef} role="group" aria-label={label} className="-mx-1.5 min-h-0 flex-1 overflow-y-auto">
                {options.map((option) => (
                    <label
                        key={String(option.value)}
                        className="hover:bg-surface-subtle flex min-h-8 cursor-pointer items-center gap-2.5 rounded-sm px-1.5 text-sm"
                    >
                        <Checkbox checked={isChecked(option)} onCheckedChange={(checked) => toggle(option, checked)} />
                        {renderLabel(option)}
                    </label>
                ))}
                {options.length === 0 ? (
                    <p className="text-muted-foreground px-1.5 py-2 text-sm">
                        {isLoading ? t('loading') : t('no-options')}
                    </p>
                ) : null}
                {after}
            </div>
        </div>
    );
}
